import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import { useLab, useStudio } from '../queries';
import { ImageLab } from '../labs/ImageLab/ImageLab';
import { WriterLab } from '../labs/WriterLab/WriterLab';
import { AnimationLab } from '../labs/AnimationLab/AnimationLab';

/**
 * Lab route per spec §4.1.
 *   - 'image-lab'   → labs/ImageLab/ImageLab.tsx (v3 shell, F13)
 *   - 'writer-lab'  → labs/WriterLab/WriterLab.tsx (F19)
 *   - anything else → "Lab not available" fallback
 *
 * Studios live as registry entries (B10 seeded `generic` / `fashion` /
 * `product-photography` on image-lab); the route reads `studioId` from the
 * URL param and threads it into ImageLab, which loads the studio via
 * `useStudio` and drives palette + agent + operation surfaces off it.
 *
 * Spike 1 polish (2026-05-26) — the route no longer wraps labs in MobileShell.
 * Each lab orchestrator now self-decomposes via `useMobileShell()` and renders
 * its own mobile layout with per-tab slot bodies. Keeps state ownership inside
 * the orchestrator and avoids the double-wrap that came from a route-level
 * shell switch.
 */
export const LabRoute: React.FC = () => {
  const { labId = 'image-lab', studioId } = useParams();

  // Per-lab default studio when the URL omits one.
  const resolvedStudioId =
    studioId ??
    (labId === 'writer-lab'
      ? 'storyteller'
      : labId === 'animation-lab'
        ? 'motion'
        : 'generic');

  const labQuery = useLab(labId);
  const studioQuery = useStudio(resolvedStudioId);

  const knownLab =
    labId === 'image-lab' || labId === 'writer-lab' || labId === 'animation-lab';

  // Feature-flag guard (SMA-13): the backend omits disabled labs from
  // /api/v3/labs/{id} and 404s disabled studios. A 404 on either means this
  // lab/studio is flagged off — fall through to the "not available" panel
  // instead of mounting it (otherwise a typed/bookmarked URL would leak a
  // hidden lab). Non-404 errors (transient) don't block — the lab handles its
  // own loading/error state. With the default all-enabled config nothing 404s.
  const gateBlocked = isNotFound(labQuery.error) || isNotFound(studioQuery.error);
  const gateResolving = knownLab && !gateBlocked && (labQuery.isLoading || studioQuery.isLoading);

  if (knownLab && !gateBlocked) {
    if (gateResolving) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (labId === 'image-lab') return <ImageLab studioId={resolvedStudioId} />;
    if (labId === 'writer-lab') return <WriterLab studioId={resolvedStudioId} />;
    if (labId === 'animation-lab') return <AnimationLab studioId={resolvedStudioId} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 560, width: '100%', borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={600}>
              {labQuery.data?.name ?? labId}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            This lab isn&apos;t available in this workspace.{' '}
            <code>{labId}</code> / <code>{resolvedStudioId}</code> is either hidden
            by a feature flag or hasn&apos;t shipped yet.
          </Typography>
          {(labQuery.isLoading || studioQuery.isLoading) && <CircularProgress size={24} />}
        </Stack>
      </Paper>
    </Box>
  );
};

/**
 * True when a React Query error is a 404 — the signal the backend uses for a
 * lab/studio that's flagged off (or genuinely missing). `callV3` rejects with
 * an `ApiResponseError` that carries a numeric `status`. Non-404 errors return
 * false so transient failures don't masquerade as "disabled".
 */
function isNotFound(err: unknown): boolean {
  return (
    !!err &&
    typeof err === 'object' &&
    (err as { status?: number }).status === 404
  );
}
